import { useForm, Controller } from 'react-hook-form'

import useMutation from '@hooks/useGQLMutation'
import {
  UpdateGroupPublicDescriptionDocument,
  type UpdateGroupPublicDescriptionMutation,
  type UpdateGroupPublicDescriptionMutationVariables,
} from '@frontend/graphql/mutations/updateGroupPublicDescription.generated'
import { useAlertsStore } from '@stores/alertsStore'

import { FormDialog } from '@components/molecules/FormDialog'
import { RichTextEditorField } from '@components/molecules/RichTextEditorField'
import Alert from '@atoms/Alert'
import { Link } from '@atoms'

import { scopedTranslation } from '@frontend/utils/I18n'
import type { EditGroupPublicDescriptionDialogState } from '../../types/dialogTypes'

const t = scopedTranslation('components.edit_group_public_description_dialog')

const ALERTS_PATH = 'editGroupPublicDescription' as const

type FormData = {
  publicDescription: string
}

type EditGroupPublicDescriptionDialogProps = {
  onClose: () => void
  dialogState: EditGroupPublicDescriptionDialogState
}

export function EditGroupPublicDescriptionDialog({ onClose, dialogState }: EditGroupPublicDescriptionDialogProps) {
  const { dialogData } = dialogState
  const { setAlerts, clearAlerts } = useAlertsStore(({ setAlerts, clearAlerts }) => ({
    setAlerts,
    clearAlerts,
  }))

  const {
    handleSubmit,
    formState: { errors },
    control,
    reset,
  } = useForm<FormData>({
    defaultValues: {
      publicDescription: dialogData.publicDescription || '',
    },
  })

  const updateGroupMutationHook = useMutation(UpdateGroupPublicDescriptionDocument)

  const onSubmit = (data: FormData) => {
    clearAlerts(ALERTS_PATH)

    const mutationData: UpdateGroupPublicDescriptionMutationVariables = {
      id: dialogData.groupId,
      publicDescription: data.publicDescription,
    }

    updateGroupMutationHook.mutate(mutationData, {
      onSuccess: (response: UpdateGroupPublicDescriptionMutation) => {
        if (response?.updateGroupPublicDescription?.success) {
          setAlerts({
            alertsPath: ALERTS_PATH,
            alerts: [
              {
                type: 'success',
                details: {
                  title: t('alerts.success.title'),
                },
              },
            ],
          })
          const updatedDescription = response.updateGroupPublicDescription.group?.publicDescription || ''
          reset({
            publicDescription: updatedDescription,
          })
        } else {
          setAlerts({
            alertsPath: ALERTS_PATH,
            alerts:
              response?.updateGroupPublicDescription?.errors?.map((error) => ({
                type: 'error',
                details: {
                  title: error.message,
                },
              })) || [],
          })
        }
      },
      onError: () => {
        setAlerts({
          alertsPath: ALERTS_PATH,
          alerts: [
            {
              type: 'error',
              details: {
                title: t('errors.update_failed'),
              },
            },
          ],
        })
      },
    })
  }

  const handleOpenChange = (nextOpen: boolean) => {
    if (!nextOpen) {
      reset()
      clearAlerts(ALERTS_PATH)
      onClose()
    }
  }

  const publicPageUrl = dialogData.publicUrl

  return (
    <FormDialog.Root
      open={true}
      onOpenChange={handleOpenChange}
      testId="edit-group-public-description-dialog"
      preventOutsideClick
      hasTinyMCE={true}
      size="2xl"
    >
      <FormDialog.Title alertsPath={ALERTS_PATH}>{t('title', { name: dialogData.groupName })}</FormDialog.Title>
      <FormDialog.Form onSubmit={handleSubmit(onSubmit)}>
        <FormDialog.Body>
          <Alert type="info" className="tw:mb-4">
            <div>
              <p className="tw:text-sm tw:mb-2">{t('public_page_info')}</p>
              {publicPageUrl && (
                <Link
                  href={publicPageUrl}
                  target="_blank"
                  rel="noopener noreferrer"
                  size="sm"
                  rank="secondary"
                  leadingIcon="eye"
                >
                  {t('view_public_page')}
                </Link>
              )}
            </div>
          </Alert>

          <Controller
            name="publicDescription"
            control={control}
            render={({ field: { ref, ...field } }) => (
              <RichTextEditorField
                label={t('form.public_description_label')}
                placeholder={t('form.placeholder.public_description')}
                errors={errors}
                {...field}
                value={field.value || ''}
              />
            )}
          />
        </FormDialog.Body>
        <FormDialog.Footer submitText={t('form.save_changes')} isLoading={updateGroupMutationHook.isLoading} />
      </FormDialog.Form>
    </FormDialog.Root>
  )
}
